import { createAsyncThunk, createAction } from '@reduxjs/toolkit'
import moment from 'moment'
import map from 'lodash/map'
import { FirewallRule, FirewallEntry } from '@lk/api'

import { actions as LKActions } from '../../../features/shared/LKModal'
import { getFirewallFilterRule } from './firewall.selectors'
import { EXPIRE_DATE_FORMAT } from './firewall.constants'
import { ThunkApi } from '../../index.d'

type MappedFirewallEntry = Omit<FirewallEntry, 'Expire'> & { Expire: string }

export const loadFirewall = createAsyncThunk<MappedFirewallEntry[], void, ThunkApi>(
  '[SECURITY] LOAD_FIREWALL_ENTRIES',
  async (_, { dispatch, getState, extra, rejectWithValue }) => {
    const rule = getFirewallFilterRule(getState())

    const result = await extra.api.security.firewalls(rule)

    if (result.success) {
      return map(result.data, (item) => ({
        ...item,
        Expire: moment.unix(item.Expire).format(EXPIRE_DATE_FORMAT),
      }))
    }

    dispatch(LKActions.openError('Failed to load firewall entries'))

    return rejectWithValue('Failed to load firewall entries')
  },
)

export const setFirewallFilterRule = createAction<FirewallRule>('[SECURITY] SET_FIREWALL_FILTER_RULE')

export const setFirewallFilterRuleAndRefetch = (rule) => (dispatch) => {
  dispatch([setFirewallFilterRule(rule), loadFirewall()])
}

interface DeleteFirewallAttributes {
  ip: string
}

export const deleteFirewall = createAsyncThunk<void, DeleteFirewallAttributes, ThunkApi>(
  '[SECURITY] DELETE_FIREWALL',
  async ({ ip }, { dispatch, getState, extra, rejectWithValue }) => {
    const rule = getFirewallFilterRule(getState())

    const result = await extra.api.security.deleteFirewall(ip, rule)

    if (result.success) {
      dispatch(loadFirewall())
      return
    }

    dispatch(LKActions.openError('Failed to delete firewall'))

    return rejectWithValue('Failed to delete firewall entries')
  },
)

export function expireDateTimestamp(date, time) {
  const [hours, minutes, seconds] = time.split(':').map(Number)

  return moment(date, 'DD.MM.YYYY')
    .add({
      hours,
      minutes,
      seconds,
    })
    .unix()
}

interface AddFirewallAttributes {
  ip: string
  mask: string
  rule: FirewallRule
  expire: boolean
  expireDate: string | void
  expireTime: string | void
  comment: string
  closeModal: Function
}

export const addFirewall = createAsyncThunk<void, AddFirewallAttributes, ThunkApi>(
  '[SECURITY] ADD_FIREWALL',
  async (
    { ip, mask, rule, expire, expireDate, expireTime, comment, closeModal },
    { dispatch, extra, rejectWithValue },
  ) => {
    const expireDiff = expire ? expireDateTimestamp(expireDate, expireTime) : 0

    const result = await extra.api.security.addFirewall(ip, mask, rule, expireDiff, comment)

    if (result.success) {
      dispatch(loadFirewall())
      closeModal()
      return
    }

    dispatch(LKActions.openError('Failed to add firewall entry'))

    return rejectWithValue('Failed to add firewall entry')
  },
)
